import { useState, useRef, useEffect, useCallback, useMemo, lazy, Suspense } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Loader2, WifiOff } from 'lucide-react'
import { toast } from 'sonner'
import { apiService } from '@/services/api'
import { CharacterSheetEditor } from './components/CharacterSheetEditor'
import { useTableDetails } from '@/hooks/useTableDetails'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useGameWebSocket } from '@/hooks/useGameWebSocket'
import { usePresence } from '@/hooks/usePresence'
import { useActivityStatus } from '@/hooks/useActivityStatus'
import { usePrivateChat } from '@/hooks/usePrivateChat'
import { useUnreadCounts } from '@/hooks/useUnreadCounts'
import { useTurnControl } from '@/hooks/useTurnControl'
import { useIsMobile } from '@/hooks/use-mobile'
import { extractIdFromSlug } from '@/utils/slugUtils'
import { isProfessorOrAdmin } from '@/utils/permissions'
import { cn } from '@/lib/utils'
import type { Character, Member } from '@/types'
import type { Member as MemberType } from '@/types/member'
import type { ChatMode, PrivateConversationSummary } from '@/types/message'
import { Player } from '../../types/turn'
import type { LiveSyncSyncMessage } from '@/modules/livesync/types'
import type { PollSyncMessage } from '@/modules/poll/types'
import type { TableModulesListResponse } from '@/modules/types'

// CSS específico para o jogo
import './GameSession.css'

// Contextos
import { TurnProvider, ChatProvider } from './contexts'
import { useTurnContextValue } from './hooks/useTurnContextValue'
import { useChatContextValue } from './hooks/useChatContextValue'

// Novos componentes
import SidePanel from './components/SidePanel'
import { PrivateInbox } from './components/PrivateInbox'
import { NotesPanel } from './components/NotesPanel'
import { ItemsPanel } from './components/ItemsPanel'
import { AbilitiesPanel } from './components/AbilitiesPanel'
// Lazy loaded components para code splitting
const MediaGallery = lazy(() => import('./components/MediaGallery').then(m => ({ default: m.MediaGallery })))
import { ChatContainer } from './GameChat/ChatContainer'
import { useMissions } from '@/hooks/useMissions'
import { DiceControls } from './GameControls/DiceControls'
import { useDiceControls } from './GameControls/useDiceControls'
import { useTurnControls } from './GameControls/useTurnControls'
import { DesktopLayout } from './GameLayout/DesktopLayout'
import { MobileLayout } from './GameLayout/MobileLayout'
import { GameSettingsModal } from './components/GameSettingsModal'
import { transicaoDeMembros } from './transicaoDeMembros'

// Importar CSS para dados
import '@/styles/dice.css'

// Módulo de Sincronização
import { DICE_MODULE_ID } from '@/modules/dice/diceModule'
import { LiveSyncViewer, LiveSyncControl } from '@/modules/livesync'

// Módulo de Enquetes
import { PollViewer, PollControl } from '@/modules/poll'

// Módulo de Aulas Interativas (module_lab)
import { LabSessionViewer, LabSessionControl, labApi, type LabSyncMessage } from '@/modules/module_lab'

export function GameSession() {
  const { tableId } = useParams<{ tableId: string }>()
  const navigate = useNavigate()
  const { user: currentUser } = useCurrentUser()
  const isMobile = useIsMobile()

  // Estado
  // (messageInput/privateMessageInput saíram — Fase 11: o texto digitado é
  // estado local do ChatInputContainer e chega aqui por argumento nos
  // handlers de envio. Digitar não re-renderiza mais o GameSession.)
  const [selectedMediaFile, setSelectedMediaFile] = useState<string | null>(null) // ID da mídia selecionada
  const [selectedMediaData, setSelectedMediaData] = useState<{ id: string; url: string; filename: string; type: string; size: number } | null>(null) // Dados completos da mídia
  const [activeTab, setActiveTab] = useState<'members' | 'chat' | 'private' | 'items' | 'abilities' | 'media' | 'notes'>('chat')
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null)

  // Estados para chat privado
  const [chatMode, setChatMode] = useState<ChatMode>('group')
  const [selectedPrivateUser, setSelectedPrivateUser] = useState<MemberType | null>(null)
  // Estado para o mestre responder COMO NPC
  const [respondingAsNpc, setRespondingAsNpc] = useState<{
    isActive: boolean
    npcUserId: string | null
    npcName: string | null
  }>({ isActive: false, npcUserId: null, npcName: null })

  // Estado para Módulo de Sincronização
  const [isLiveSyncControlOpen, setIsLiveSyncControlOpen] = useState(false)
  // Cerimônia de sorteio (fase 3a): quem está sendo sorteado, ficha em revelação
  const [sorteandoUserId, setSorteandoUserId] = useState<string | null>(null)
  const [revealCharacter, setRevealCharacter] = useState<Character | null>(null)
  const sorteioNudgeShownRef = useRef(false) // nudge do sorteio: só uma vez por sessão
  const muteToastShownRef = useRef(false) // aviso ao aluno mudo: só uma vez
  const [liveSyncMessage, setLiveSyncMessage] = useState<LiveSyncSyncMessage | undefined>(undefined)

  // Estado para Módulo de Enquetes
  const [isPollControlOpen, setIsPollControlOpen] = useState(false)
  const [pollMessage, setPollMessage] = useState<PollSyncMessage | undefined>(undefined)

  // Estado para Módulo de Aulas Interativas (module_lab)
  const [isLabControlOpen, setIsLabControlOpen] = useState(false)
  const [labMessage, setLabMessage] = useState<LabSyncMessage | undefined>(undefined)
  
  // Estado para Modal de Configurações do Jogo
  const [showGameSettings, setShowGameSettings] = useState(false)
  // Estado de turnos agora gerenciado pelo hook useTurnControls

  // Extrair ID real da tabela do slug
  const realTableId = tableId ? extractIdFromSlug(tableId) : ""

  // Modulos ativados na mesa (gating das acoes rapidas — regra 2026-07-23:
  // acao de modulo so aparece se o mestre ativou o modulo no gerenciador).
  // Busca unica no mount; falha degrada para nenhum modulo (sessao intacta).
  const [enabledModules, setEnabledModules] = useState<string[]>([])
  useEffect(() => {
    if (!realTableId) return
    let cancelled = false
    apiService.request<TableModulesListResponse>(
      `/modules/table/${realTableId}`,
      { method: 'GET' }
    )
      .then(response => {
        if (cancelled) return
        setEnabledModules(
          response.modules.filter(m => m.is_active).map(m => m.module_id)
        )
      })
      .catch(err => {
        console.error('Erro ao carregar modulos da mesa (acoes de modulo ocultas):', err)
      })
    return () => { cancelled = true }
  }, [realTableId])

  // Envia ao chat a rolagem congelada na cerimonia. O servidor monta a frase
  // a partir da linha gravada — daqui vai so o id.
  const handleEnviarRolagem = useCallback(async (rollId: string) => {
    try {
      await apiService.request(`/dice/${rollId}/publicar`, { method: 'POST' })
      return true
    } catch (erro) {
      console.error('Erro ao enviar a rolagem ao chat:', erro)
      return false
    }
  }, [])

  // Modulo de dados (docs/PLANO_MODULO_DADOS.md): nem todo jogo usa dado.
  // Desligado, some a acao rapida, some o "pedir teste" da ficha e o modal
  // nao e montado. O servidor recusa junto (403) — a UI so evita o caminho.
  const dadosAtivos = enabledModules.includes(DICE_MODULE_ID)

  // Hook para gerenciar controles de dados
  const {
    showDiceModal,
    sharedDiceModal,
    sharedDiceRolling,
    repetida: jogadaRepetida,
    handleRollDice,
    handleDiceModalClose,
    handleSharedDiceModalCloseRequest,
    handleSharedDiceRollStart,
    handleSharedDiceRollEnd,
    handleRolagemPublicada,
    handleRolagemRepetida,
    handleTrayEspelhada,
    handleSharedDiceModalOpen,
    handleSharedDiceModalClose
  } = useDiceControls({
    tableId: realTableId || '',
    currentUser,
    selectedCharacter
  })

  // Table details
  const { table, setTable, characters, setCharacters, members, setMembers, isLoading } = useTableDetails(realTableId, currentUser || undefined)

  // Handler para atualização de stats de personagens em tempo real
  // (chega via callback do useGameWebSocket — antes era um segundo
  // addEventListener aqui, parseando toda mensagem do socket 2x)
  const handleCharacterStatsUpdate = useCallback((data: {
    character_id: string
    character_name?: string
    updates: {
      hp_current?: number
      hp_total?: number
      points?: number
      level?: number
      attributes?: Record<string, number>
      name?: string // rename em tempo real (batismo da cerimônia / edição)
      class_name?: string // classe editada pelo mestre na ficha
    }
  }) => {
    setCharacters(prev => prev.map(char => {
      if (char.id === data.character_id) {
        const updates = data.updates
        return {
          ...char,
          name: updates.name ?? char.name,
          className: updates.class_name ?? char.className,
          hpCurrent: updates.hp_current ?? char.hpCurrent,
          hpTotal: updates.hp_total ?? char.hpTotal,
          points: updates.points ?? char.points,
          level: updates.level ?? char.level,
          attributes: updates.attributes
            ? { ...char.attributes, ...updates.attributes }
            : char.attributes
        }
      }
      return char
    }))

    if (data.character_name) {
      toast.info(`📊 ${data.character_name} teve seus stats atualizados`)
    }
  }, [setCharacters])

  // Handler para novo membro entrando na mesa
  const handleMemberJoined = useCallback((newMember: Member) => {
    setMembers(prev => {
      // Evitar duplicatas
      if (prev.some(m => m.userId === newMember.userId)) {
        return prev
      }
      return [...prev, newMember]
    })
    toast.info(`${newMember.userFullName} entrou na mesa`)
  }, [setMembers])

  // Handler para novo personagem criado na mesa
  const handleCharacterCreated = useCallback((newCharacter: Character) => {
    setCharacters(prev => {
      // Evitar duplicatas
      if (prev.some(c => c.id === newCharacter.id)) {
        return prev
      }
      return [...prev, newCharacter]
    })
    toast.info(`🎭 ${newCharacter.name} entrou na aventura!`)
    // Revelação cerimonial (sorteio mediado): depois do dado terminar de girar,
    // a ficha do personagem sorteado abre para TODOS (decisão do Paulo).
    if ((newCharacter as any).sorteio) {
      setTimeout(() => setRevealCharacter(newCharacter), 2800)
    }
  }, [setCharacters])

  // WebSocket para chat em tempo real
  const {
    messages,
    isConnected,
    isReconnecting,
    connectionError,
    reconnect,
    sendMessage: sendWebSocketMessage,
    updateMessage,
    rollDice,
    loadingHistory,
    loadMoreMessages,
    reloadHistory,
    loadingMoreMessages,
    hasMoreMessages,
    websocket,
    subscribe
  } = useGameWebSocket({
    tableId: realTableId || '',
    userId: currentUser?.id,
    token: localStorage.getItem('auth_token') || undefined,
    onDiceRollPublished: handleRolagemPublicada,
    onDiceRollStart: handleSharedDiceRollStart,
    onDiceRollEnd: handleSharedDiceRollEnd,
    onDiceModalOpen: handleSharedDiceModalOpen,
    onDiceModalClose: handleSharedDiceModalClose,
    onDiceTrayUpdate: handleTrayEspelhada,
    onDiceRollRepeated: handleRolagemRepetida,
    onLiveSyncSync: (data) => setLiveSyncMessage(data),
    onPollSync: (data) => setPollMessage(data),
    onLabSync: (data) => {
      setLabMessage(data)
      // limpa estado após 'end' pra próximo recovery não disparar
      if (data?.action === 'end') {
        setTimeout(() => setLabMessage(undefined), 100)
      }
    },
    onMemberJoined: handleMemberJoined,
    onCharacterCreated: handleCharacterCreated,
    onCharacterStatsUpdate: handleCharacterStatsUpdate
  })

  // Missões (capítulos). Reusa o hub `subscribe` do socket (sem novo
  // listener). Ver docs/PLANO_MISSOES.md.
  //
  // A `MissionSessionBar` que exibia isto tudo saiu em 2026-07-27: era uma
  // barra inteira na coluna do chat para uma linha de texto. O título subiu
  // para a `TopNavBar` e os controles de iniciar/encerrar sessão foram para o
  // painel de ações rápidas do mestre (docs/PLANO_AREA_DO_CHAT.md, fase 2).
  const {
    activeMission,
    openSession,
    loaded: missionsLoaded,
    startSession: startMissionSession,
    endSession: endMissionSession,
  } = useMissions(realTableId, subscribe)

  // Chat carrega só a missão ativa quando ela existe; sem missão ativa, mantém
  // o fluxo atual (sem filtro — I1). Só recarrega quando o filtro muda de fato
  // (reloadHistory é no-op se o filtro já é o mesmo). Espera as missões
  // carregarem (missionsLoaded) para não filtrar antes de saber o estado.
  useEffect(() => {
    if (!missionsLoaded) return
    reloadHistory(activeMission?.id)
  }, [missionsLoaded, activeMission?.id, reloadHistory])

  // Recovery do module_lab: ao entrar na mesa, verifica se há aula 'live'.
  // Útil quando o mestre/aluno deu refresh ou voltou pra mesa sem encerrar.
  useEffect(() => {
    if (!realTableId) return
    let cancelled = false
    labApi.getActiveForTable(realTableId)
      .then((active) => {
        if (cancelled || !active) return
        setLabMessage({
          type: 'lab_sync',
          action: 'start',
          data: {
            session_id: active.id,
            game_slug: active.game_slug,
            table_id: active.table_id,
          },
        })
      })
      .catch((err) => {
        if (!cancelled) console.warn('[LAB] recovery falhou:', err)
      })
    return () => { cancelled = true }
  }, [realTableId])

  // Hook de presença para rastrear usuários online
  const { isUserOnline } = usePresence({
    tableId: realTableId || '',
    subscribe,
    isConnected
  })

  // Sistema de status de atividade (online/away/offline) — usado só pelo
  // efeito colateral de enviar status_update ao servidor
  useActivityStatus({
    websocket,
    isConnected,
    enabled: !!realTableId && !!currentUser
  })

  // Hook para contagens não lidas
  const {
    unreadCounts,
    totalUnread,
    markUserMessagesAsRead,
    incrementUnreadCount,
    fetchUnreadCounts
  } = useUnreadCounts({
    tableId: realTableId,
    currentUserId: currentUser?.id || null,
    enabled: isConnected
  })

  // Não lidas do chat em grupo: contador local de mensagens que chegam
  // enquanto a aba Chat não está visível; zera ao abrir a aba. (O loadMore
  // prepende mensagens antigas e não altera a última — não conta.)
  const [groupUnread, setGroupUnread] = useState(0)
  const groupChatVisible = activeTab === 'chat' && chatMode === 'group'
  const lastGroupMsgIdRef = useRef<string | null>(null)
  useEffect(() => {
    const last = messages[messages.length - 1]
    if (!last || last.id === lastGroupMsgIdRef.current) return
    const isFirstLoad = lastGroupMsgIdRef.current === null
    lastGroupMsgIdRef.current = last.id
    if (isFirstLoad || groupChatVisible) return
    if (last.userId !== currentUser?.id) {
      setGroupUnread(c => c + 1)
    }
  }, [messages, groupChatVisible, currentUser?.id])
  useEffect(() => {
    if (groupChatVisible) setGroupUnread(0)
  }, [groupChatVisible])

  // Refs para dados sempre atualizados no callback de mensagem privada
  const membersRef = useRef(members)
  const charactersRef = useRef(characters)
  membersRef.current = members
  charactersRef.current = characters

  // Mapa de toast IDs persistentes por senderId — permite dismiss ao abrir conversa
  const privateMessageToastIds = useRef<Map<string, string | number>>(new Map())
  // Ref para connectPrivateRoom: usado no action onClick do toast (executa após hooks)
  const connectPrivateRoomRef = useRef<((userId: string) => Promise<void>) | null>(null)

  // Callback: incrementa contador + toast persistente com ação "Ver"
  const handleNewPrivateMessage = useCallback((senderId: string) => {
    incrementUnreadCount(senderId)
    const member = membersRef.current.find(m => m.userId === senderId)
    const character = charactersRef.current.find(c => c.userId === senderId)
    const senderName = character?.name || member?.userFullName || 'Usuário'

    // Dismiss toast anterior do mesmo remetente (evita duplicatas)
    const existingId = privateMessageToastIds.current.get(senderId)
    if (existingId !== undefined) {
      toast.dismiss(existingId)
    }

    const toastId = toast.info(`💬 ${senderName} enviou uma mensagem privada`, {
      duration: Infinity,
      closeButton: true,
      action: {
        label: 'Ver',
        onClick: () => {
          // Dismiss via mapa (toastId já está definido ao rodar)
          const id = privateMessageToastIds.current.get(senderId)
          if (id !== undefined) {
            toast.dismiss(id)
            privateMessageToastIds.current.delete(senderId)
          }
          if (member) {
            setChatMode('private')
            setSelectedPrivateUser(member)
            setActiveTab('chat')
            markUserMessagesAsRead(senderId)
            connectPrivateRoomRef.current?.(senderId).then(() => fetchUnreadCounts())
          }
        }
      }
    })
    privateMessageToastIds.current.set(senderId, toastId)
  }, [incrementUnreadCount, markUserMessagesAsRead, fetchUnreadCounts])

  // Hook de chat privado
  const {
    privateMessages,
    conversations,
    activeConversationUserId,
    loadingHistory: loadingPrivateHistory,
    sendPrivateMessage,
    sendPrivateMessageAsNpc,
    loadPrivateHistory,
    loadPrivateHistoryAsNpc,
    loadConversations,
    loadNpcConversations,
    connectPrivateRoom,
    disconnectPrivateRoom
  } = usePrivateChat({
    tableId: realTableId || '',
    currentUserId: currentUser?.id,
    subscribe,
    onNewMessage: handleNewPrivateMessage
  })
  connectPrivateRoomRef.current = connectPrivateRoom

  // Converter membros para formato de players do sistema de turnos
  // Prioriza nome do personagem, fallback para nome do membro.
  // useMemo: o GameSession re-renderiza a cada mensagem de chat — sem memo,
  // estes arrays ganham identidade nova toda vez e invalidam o
  // turnContextValue (re-render de todos os consumidores do TurnContext).
  const players: Player[] = useMemo(() => members
    .filter(member => !member.isNpc) // Apenas membros reais (não NPCs)
    .map(member => {
      // Buscar personagem do membro (excluindo NPCs)
      const memberCharacter = characters.find(c => c.userId === member.userId && !c.isNpc)
      return {
        id: member.userId,
        name: memberCharacter?.name || member.userFullName,
        avatar: memberCharacter?.imageUrl || undefined,
        isNpc: false,
        // Presenca agora: quem esta fora da mesa aparece na tela, mas
        // desmarcado (a rodada nasce com quem esta).
        online: isUserOnline(member.userId)
      }
    }), [members, characters, isUserOnline])

  // Lista de NPCs para o sistema de turnos
  const npcParticipants: Player[] = useMemo(() => characters
    .filter(c => c.isNpc)
    .map(npc => ({
      id: `npc_${npc.id}`,  // Prefixo para diferenciar de usuários
      name: npc.name,
      avatar: npc.imageUrl || undefined,
      isNpc: true,
      characterId: npc.id
    })), [characters])

  // Lista unificada de participantes elegíveis para turnos (jogadores + NPCs)
  const turnParticipants: Player[] = useMemo(
    () => [...players, ...npcParticipants],
    [players, npcParticipants]
  )

  // Sistema de turnos - identificar mestre
  const masterId = members.find(m => m.role === 'mestre')?.userId
  const isMaster = Boolean(currentUser?.id && masterId && currentUser.id === masterId)

  // Sem mestre na mesa, ninguem rola (fase B do modulo de dados, decisao do
  // Paulo em 2026-07-26). O SERVIDOR e a autoridade — recusa com 403 olhando as
  // conexoes vivas; esta conta e so para a bandeja poder EXPLICAR antes de o
  // jogador tentar. O mestre passa: ele e quem valida.
  const mestreOnline = isMaster || Boolean(masterId && isUserOnline(masterId))

  // Hook de controle de turnos
  const {
    turnState,
    permissions,
    currentPlayer,
    startTurn,
    startCountdown,
    pauseTurn,
    goToNextPlayer,
    adjustTime,
    endTurn,
    formatTime,
  } = useTurnControl({
    tableId: realTableId || '',
    players,
    masterId,
    websocket,
    subscribe,
    isConnected
  })

  // Cerimônia de sorteio (criação mediada, fase 3a): o gatilho vive na lista de
  // jogadores do SidePanel ("Aguardando sorteio"); o sorteio roda no servidor e
  // o ritual é transmitido pela mesa (dado girando + revelação da ficha).
  const ceremonyMediada = table?.character_creation_open === false

  // Ficha do próprio usuário na mesa. O aluno nunca "seleciona" a dele:
  // selectedCharacter só é preenchido pelo seletor de NPC, que é do mestre.
  const meuPersonagem = useMemo(
    () => characters.find(c => c.userId === currentUser?.id && !c.isNpc) ?? null,
    [characters, currentUser?.id]
  )

  // Mute da criação mediada (espelha a regra do servidor): aluno sem ficha não
  // fala até ser sorteado — o input fica desabilitado com aviso único.
  const chatMuted = Boolean(
    ceremonyMediada && !isMaster && currentUser &&
    !characters.some(c => c.userId === currentUser.id && !c.isNpc)
  )

  const handleSortearCerimonia = async (userId: string) => {
    if (!realTableId) return
    setSorteandoUserId(userId)
    try {
      // O broadcast (dice_roll_start/end + character_created com sorteio:true)
      // cuida do dado, da revelação da ficha e da troca do nome.
      await apiService.sortearCerimonia(realTableId, userId)
    } catch (error: any) {
      toast.error(error?.message || 'Não foi possível sortear')
    } finally {
      setSorteandoUserId(null)
    }
  }

  // Batismo (cerimônia): o sorteado dá nome ao próprio herói na revelação.
  // O PUT propaga o novo nome pela mesa (canal de stats, silencioso).
  const handleBatizar = async (nome: string) => {
    if (!revealCharacter) return
    try {
      await apiService.updateCharacter(revealCharacter.id, { name: nome })
      setCharacters(prev => prev.map(c => c.id === revealCharacter.id ? { ...c, name: nome } : c))
      setRevealCharacter(prev => prev ? { ...prev, name: nome } : prev)
      toast.success(`⚔️ ${nome} entrou para a lenda!`)
    } catch {
      toast.error('Não foi possível batizar o personagem')
    }
  }

  // Ativa a criação mediada (a partir do nudge) — aí o sorteio aparece na lista.
  const handleAtivarSorteioMediado = async () => {
    if (!realTableId) return
    try {
      await apiService.setCharacterCreationOpen(realTableId, false)
      setTable(prev => prev ? { ...prev, character_creation_open: false } : prev)
      toast.success('Sorteio mediado ativado. Sorteie cada jogador na lista (aba Membros).')
    } catch {
      toast.error('Não foi possível ativar o sorteio mediado')
    }
  }

  // Nudges (uma vez por sessão): mestre com alunos sem ficha; aluno mudo.
  useEffect(() => {
    if (isLoading) return
    if (isMaster && !sorteioNudgeShownRef.current) {
      // Só membros de papel "jogador" contam — a lista `players` inclui o
      // mestre (que normalmente não tem ficha) e inflava a contagem do toast.
      // Mesmo filtro da seção "Aguardando sorteio" do SidePanel.
      const semFicha = members.filter(m =>
        m.role === 'jogador' && m.isActive !== false &&
        !characters.some(c => c.userId === m.userId && !c.isNpc)
      )
      if (semFicha.length > 0) {
        sorteioNudgeShownRef.current = true
        if (ceremonyMediada) {
          toast.info(`${semFicha.length} jogador(es) aguardando sorteio — veja a lista de jogadores (aba Membros).`)
        } else {
          toast.info(`${semFicha.length} jogador(es) sem personagem.`, {
            duration: 8000,
            action: { label: 'Ativar sorteio mediado', onClick: () => { void handleAtivarSorteioMediado() } },
          })
        }
      }
    }
    if (chatMuted && !muteToastShownRef.current) {
      muteToastShownRef.current = true
      toast.info('Aguarde o mestre sortear seu personagem — o chat abre depois do sorteio.', { duration: 8000 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isMaster, members, characters, ceremonyMediada, chatMuted])


  // Hook para gerenciar controles de turnos
  const {
    handleStartTurn,
    handleStartCountdown
  } = useTurnControls({
    startTurn,
    startCountdown
  })

  // Nota: o refresh do turn state ao conectar é feito dentro de
  // useTurnControl (chama refreshTurnState + loadTurnStateHTTP no listener).
  // Não duplicar aqui.

  useEffect(() => {
    if (!realTableId) {
      toast.error('ID da mesa inválido')
      navigate('/pagina-inicial')
      return
    }
  }, [realTableId, navigate])

  // Handlers
  // Fase 11: o texto chega por argumento (estado local do ChatInputContainer).
  // Sem a dep em messageInput, este callback fica estável entre keystrokes e
  // o chatContextValue não é mais invalidado a cada tecla digitada.
  const handleSendMessage = useCallback((text: string): boolean => {
    if ((!text.trim() && !selectedMediaFile) || !currentUser) {
      return false
    }

    const success = sendWebSocketMessage(
      text || '',
      selectedCharacter ? 'player' : 'master',
      selectedCharacter?.id,
      selectedMediaFile || undefined,
      selectedMediaData || undefined,
      selectedCharacter?.name // Passar o nome do personagem para a mensagem otimista
    )

    if (success) {
      setSelectedMediaFile(null)
      setSelectedMediaData(null)
    } else {
      toast.error('Erro ao enviar mensagem. Verifique a conexão.')
    }
    return success
  }, [selectedMediaFile, selectedMediaData, currentUser, sendWebSocketMessage, selectedCharacter])

  const handleMediaUpload = useCallback((mediaFile: { id: string; url: string; filename: string; type: string; size: number }) => {
    // Quando mídia é compartilhada da galeria, enviar automaticamente ao chat
    const success = sendWebSocketMessage(
      '', // Sem texto, apenas mídia
      selectedCharacter ? 'player' : 'master',
      selectedCharacter?.id,
      mediaFile.id,
      mediaFile,
      selectedCharacter?.name // Passar o nome do personagem para a mensagem otimista
    )

    if (!success) {
      toast.error('Erro ao enviar mídia ao chat. Verifique a conexão.')
    }
  }, [sendWebSocketMessage, selectedCharacter])

  // Handlers de dados agora vêm do hook useDiceControls

  // "Membros" saiu daqui como exceção (2026-07-31): era o único dos sete
  // destinos da barra que abria um painel por cima em vez de trocar de aba.
  // Agora cai no ramo padrão, como Itens e Anotações. Consequência declarada:
  // ir de uma conversa privada para Membros passa a desconectar do quarto
  // privado, que é o que já acontecia ao ir para qualquer outra aba.
  const handleTabChange = useCallback((tab: 'members' | 'chat' | 'private' | 'items' | 'abilities' | 'media' | 'notes') => {
    const aplicar = () => {
    if (tab === 'chat') {
      // Aba chat - sempre volta para o chat em grupo
      // (o rascunho do input privado é descartado pelo próprio
      // ChatInputContainer quando a conversa muda)
      setChatMode('group')
      setSelectedPrivateUser(null)
      setActiveTab('chat')
      // Desconectar da conversa privada se estava em uma
      disconnectPrivateRoom()
    } else if (tab === 'media') {
      // Aba mídias - mostra galeria de mídias
      setActiveTab('media')
      setChatMode('group') // Garantir que volta para chat em grupo
    } else if (tab === 'private') {
      // Aba privado: inbox de conversas entre participantes (as anotações
      // pessoais saíram daqui — terão área própria)
      setChatMode('group')
      setSelectedPrivateUser(null)
      disconnectPrivateRoom()
      setActiveTab('private')
      loadConversations()
    } else {
      // Para as demais (members, items, abilities, notes): muda a aba. Precisa
      // resetar chatMode: se o usuario estava num chat privado, o input
      // fixo (renderizado quando chatMode==='private') ficava sobreposto
      // a aba Itens no mobile.
      setChatMode('group')
      setSelectedPrivateUser(null)
      disconnectPrivateRoom()
      setActiveTab(tab)
    }
    }

    // Entrar e sair da aba de Membros desliza (2026-07-31, pedido do Paulo:
    // "quando clico para abrir a lista de membros nenhum efeito"). A primeira
    // rodada animou só as trocas DENTRO da aba; a abertura pela barra passa
    // por aqui, e ficava seca justamente nos navegadores com a API — neles o
    // fallback de entrada é desligado. As demais abas seguem como sempre.
    const direcao =
      tab === 'members' && activeTab !== 'members' ? 'abre'
      : activeTab === 'members' && tab !== 'members' ? 'fecha'
      : null
    if (direcao && isMobile) {
      transicaoDeMembros(direcao, aplicar)
    } else {
      aplicar()
    }
  }, [activeTab, isMobile, disconnectPrivateRoom, loadConversations])

  // Handlers para chat privado
  const handleMemberClick = useCallback(async (member: MemberType) => {
    // Dismiss toast persistente deste usuário (se existir)
    const existingToastId = privateMessageToastIds.current.get(member.userId)
    if (existingToastId !== undefined) {
      toast.dismiss(existingToastId)
      privateMessageToastIds.current.delete(member.userId)
    }

    setChatMode('private')
    setSelectedPrivateUser(member)
    setActiveTab('chat')

    // Marcar localmente, conectar (carrega histórico + marca no backend),
    // e só então atualizar contadores.
    markUserMessagesAsRead(member.userId)
    await connectPrivateRoom(member.userId)
    fetchUnreadCounts()
  }, [markUserMessagesAsRead, connectPrivateRoom, fetchUnreadCounts])

  // Handler para chat privado via clique no personagem
  const handleCharacterChatClick = useCallback(async (character: Character) => {
    if (!currentUser) return

    let targetUserId: string
    let targetUserName: string
    let isTargetNpc = false

    // Lógica de redirecionamento:
    // 1. Se é NPC → usa o userId do próprio NPC (conversa separada!)
    // 2. Caso contrário → chat com o dono do personagem
    // (o próprio personagem não abre nada — chat privado é entre participantes)

    if (character.isNpc) {
      // NPC: usar o userId do próprio NPC para conversa separada
      // Cada NPC tem seu próprio userId no formato npc_{tableId}_{uuid}
      targetUserId = character.userId
      targetUserName = character.name
      isTargetNpc = true
    } else if (character.userId === currentUser.id) {
      return
    } else {
      // Personagem de outro jogador
      targetUserId = character.userId
      const memberOwner = members.find(m => m.userId === character.userId)
      targetUserName = memberOwner?.userFullName || character.name
    }

    // Dismiss toast persistente deste usuário (se existir)
    const existingToastId = privateMessageToastIds.current.get(targetUserId)
    if (existingToastId !== undefined) {
      toast.dismiss(existingToastId)
      privateMessageToastIds.current.delete(targetUserId)
    }

    // Criar um objeto "member" para usar no chat privado
    const targetMember: MemberType = {
      id: `chat-${targetUserId}`,
      userId: targetUserId,
      userFullName: targetUserName,
      role: isTargetNpc ? 'npc' : (targetUserId === masterId ? 'mestre' : 'jogador'),
      isActive: true,
      isNpc: isTargetNpc,
      controlledByUserId: isTargetNpc ? character.controlledByUserId : undefined
    }

    setChatMode('private')
    setSelectedPrivateUser(targetMember)
    setActiveTab('chat')

    markUserMessagesAsRead(targetUserId)
    await connectPrivateRoom(targetUserId)
    fetchUnreadCounts()
  }, [currentUser, members, masterId, markUserMessagesAsRead, connectPrivateRoom, fetchUnreadCounts])

  // Abrir conversa a partir do inbox da aba Privado
  const handleOpenConversation = useCallback((conv: PrivateConversationSummary) => {
    handleMemberClick({
      id: `chat-${conv.user_id}`,
      userId: conv.user_id,
      userFullName: conv.user_full_name,
      role: conv.is_npc ? 'npc' : (conv.user_id === masterId ? 'mestre' : 'jogador'),
      isActive: true,
      isNpc: conv.is_npc,
      controlledByUserId: conv.controlled_by_user_id
    })
  }, [handleMemberClick, masterId])

  const handleBackToGroup = useCallback(() => {
    // Desconectar da conversa privada
    disconnectPrivateRoom()

    // Voltar da conversa para o inbox de conversas (o rascunho do input
    // privado é descartado pelo próprio ChatInputContainer quando a
    // conversa muda)
    setChatMode('group')
    setSelectedPrivateUser(null)
    setActiveTab('private')
    loadConversations()
    // Limpar estado de "respondendo como NPC"
    setRespondingAsNpc({ isActive: false, npcUserId: null, npcName: null })
  }, [disconnectPrivateRoom, loadConversations])

  // Fase 11: texto por argumento — sem dep no input, callback estável.
  const handleSendPrivateMessage = useCallback(async (text: string): Promise<boolean> => {
    if (!text.trim() || !selectedPrivateUser || !currentUser) return false

    let success: boolean

    // Se o mestre está respondendo COMO NPC, usar endpoint específico
    if (respondingAsNpc.isActive && respondingAsNpc.npcUserId) {
      success = await sendPrivateMessageAsNpc(
        respondingAsNpc.npcUserId,
        selectedPrivateUser.userId,
        text.trim()
      )
    } else {
      success = await sendPrivateMessage(
        selectedPrivateUser.userId,
        text.trim()
      )
    }

    if (!success) {
      toast.error('Erro ao enviar mensagem privada. Tente novamente.')
    }
    return success
  }, [selectedPrivateUser, currentUser, respondingAsNpc, sendPrivateMessageAsNpc, sendPrivateMessage])

  // Handler para o mestre alternar para responder como NPC
  const handleToggleRespondAsNpc = useCallback((npc: Character | null) => {
    if (!npc) {
      // Desativar modo NPC
      setRespondingAsNpc({ isActive: false, npcUserId: null, npcName: null })
    } else {
      // Ativar modo NPC
      setRespondingAsNpc({
        isActive: true,
        npcUserId: npc.userId,
        npcName: npc.name
      })
    }
  }, [])

  const turnContextValue = useTurnContextValue({
    turnState,
    currentPlayer,
    permissions,
    turnParticipants,
    adjustTime,
    goToNextPlayer,
    endTurn,
    handleStartTurn,
    pauseTurn,
    handleStartCountdown,
    formatTime,
  })

  const openLiveSyncControl = useCallback(() => setIsLiveSyncControlOpen(true), [])
  const openPollControl = useCallback(() => setIsPollControlOpen(true), [])
  const openLabControl = useCallback(() => setIsLabControlOpen(true), [])
  const openGameSettings = useCallback(() => setShowGameSettings(true), [])
  const closeGameSettings = useCallback(() => setShowGameSettings(false), [])

  // Compartilhar mídia da galeria no chat em grupo (usado em onSelectMedia e
  // onSendToChat — estável para não invalidar o memo da MediaGallery)
  const handleMediaShared = useCallback((media: { id: string; url: string; filename: string; type: string; size: number }) => {
    handleMediaUpload({
      id: media.id,
      url: media.url,
      filename: media.filename,
      type: media.type,
      size: media.size
    })
    setChatMode('group')
    setActiveTab('chat')
  }, [handleMediaUpload])

  // "Missão 3: O laboratório" — o que a barra da missão dizia, agora na barra
  // do topo, sem gastar uma linha própria da coluna do chat.
  const tituloDaMissao = activeMission
    ? `Missão ${activeMission.order}: ${activeMission.title}`
    : undefined

  // O que a `MissionSessionBar` oferecia ao mestre, agora no painel de ações
  // rápidas. `null` sem missão ativa: sem capítulo não há sessão a abrir, e
  // linha eternamente bloqueada é ruído (o mesmo princípio da fase 6).
  const sessaoDaMissao = useMemo(() => (
    activeMission
      ? {
          aberta: Boolean(openSession),
          onIniciar: startMissionSession,
          onEncerrar: endMissionSession,
        }
      : null
  ), [activeMission, openSession, startMissionSession, endMissionSession])

  // A dica "nenhuma missão ativa" era uma barra permanente na coluna do chat,
  // repetindo a mesma frase a aula inteira. Vira um aviso, uma vez por mesa:
  // quem precisa da informação é o mestre, e uma vez basta.
  const mesaJaAvisada = useRef<string | null>(null)
  useEffect(() => {
    if (!missionsLoaded || !isMaster || activeMission || !realTableId) return
    if (mesaJaAvisada.current === realTableId) return
    mesaJaAvisada.current = realTableId
    toast.info('Nenhuma missão ativa nesta mesa.', {
      description: 'Ative uma na página da campanha para organizar este capítulo.',
    })
  }, [missionsLoaded, isMaster, activeMission, realTableId])

  const chatContextValue = useChatContextValue({
    chatMode,
    selectedCharacter,
    selectedPrivateUser,
    selectedMediaFile,
    selectedMediaData,
    respondingAsNpc,
    isConnected,
    isReconnecting,
    loadingHistory,
    loadingPrivateHistory,
    isMaster,
    setSelectedCharacter,
    handleSendMessage,
    handleSendPrivateMessage,
    handleMediaUpload,
    handleToggleRespondAsNpc,
    handleRollDice,
    openLiveSyncControl,
    openPollControl,
    openLabControl,
    enabledModules,
    sessaoDaMissao,
    realTableId: realTableId || '',
  })

  // Conteúdo das abas que NÃO são o chat. O chat fica sempre montado (ver
  // tabContent) — desmontá-lo a cada troca de aba re-renderizava a lista
  // inteira de mensagens e perdia o estado interno ao voltar.
  // useMemo para que tabContent (também memoizado) tenha uma dependência
  // estável e o memo do DesktopLayout possa de fato pular re-renders.
  const secondaryTabContent = useMemo(() => {
    switch (activeTab) {
      // "Membros" é uma aba como as outras desde 2026-07-31
      // (docs/PLANO_MEMBROS_E_FICHA.md). Este ramo existia e era código morto:
      // o handleTabChange interceptava 'members' para abrir um Drawer por cima,
      // e o rail do desktop nem lista Membros. Agora é o caminho vivo do
      // celular — e por isso ganhou o `isUserOnline`, que faltava.
      case 'members':
        return (
          <div className="h-full">
            <SidePanel
              characters={characters}
              members={members}
              tableId={realTableId || undefined}
              tableName={table?.name}
              userFullName={currentUser?.full_name || currentUser?.username || ''}
              userRole={currentUser ? isProfessorOrAdmin(currentUser) ? 'Mestre' : 'Jogador' : 'Jogador'}
              onCharacterChatClick={handleCharacterChatClick}
              currentUserId={currentUser?.id || ''}
              unreadCounts={unreadCounts}
              // Sem isto a lista perde as bolinhas de online/offline e a
              // ordenação por presença — e nada quebra, nenhum erro aparece.
              isUserOnline={isUserOnline}
              // No celular só cabe uma tela: a ficha substitui a lista, com
              // "‹ Membros" para voltar. Quem monta decide — o SidePanel não
              // adivinha onde está.
              fichaComo={isMobile ? 'empilhada' : 'sobreposta'}
              sorteioMediada={ceremonyMediada}
              dadosAtivos={dadosAtivos}
              onSortear={isMaster ? handleSortearCerimonia : undefined}
              sorteandoUserId={sorteandoUserId}
            />
          </div>
        )

      case 'private':
        return (
          <div className="h-full">
            <PrivateInbox
              conversations={conversations}
              masterUserId={masterId}
              unreadCounts={unreadCounts}
              isUserOnline={isUserOnline}
              onOpenConversation={handleOpenConversation}
            />
          </div>
        )

      case 'notes':
        return (
          <div className="h-full">
            <NotesPanel tableId={realTableId || ''} />
          </div>
        )

      case 'items':
        return (
          <div className="h-full">
            <ItemsPanel
              tableId={realTableId || ''}
              characters={characters}
              currentUserId={currentUser?.id || ''}
              isMaster={isMaster}
              subscribe={subscribe}
            />
          </div>
        )

      case 'abilities':
        return (
          <div className="h-full">
            <AbilitiesPanel
              tableId={realTableId || ''}
              characters={characters}
              currentUserId={currentUser?.id || ''}
              isMaster={isMaster}
              subscribe={subscribe}
            />
          </div>
        )

      case 'media':
        return (
          <div className="h-full">
            <Suspense fallback={
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-6 w-6 animate-spin text-violet-500" />
              </div>
            }>
              <MediaGallery
                tableId={realTableId || ''}
                userId={currentUser?.id || ''}
                onSelectMedia={handleMediaShared}
                onSendToChat={handleMediaShared}
              />
            </Suspense>
          </div>
        )

      default:
        return null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, characters, members, realTableId, table?.name, currentUser, handleCharacterChatClick, unreadCounts, handleMediaShared, ceremonyMediada, isMaster, sorteandoUserId, conversations, masterId, isUserOnline, handleOpenConversation, subscribe])

  // Conteúdo renderizado (children para os layouts). Fase B mobile: sem
  // padding compensatório — nos dois layouts o input/nav vivem no fluxo da
  // coluna flex e reservam o próprio espaço por construção (antes o mobile
  // usava barras fixed + pb-16/28/32/44 chutados, que escondiam mensagens
  // quando o input crescia).
  // O ChatContainer fica sempre montado e é escondido via CSS nas outras
  // abas — evita re-renderizar a MessageList inteira a cada volta pro chat.
  // useMemo: children estável entre renders do GameSession que não alteram
  // o chat (presença, unread) — deixa o memo dos layouts funcionar.
  const tabContent = useMemo(() => (
    <div className="h-full flex flex-col">
      <div className={cn('h-full min-h-0 flex-col overflow-hidden', activeTab === 'chat' ? 'flex' : 'hidden')}>
        <ChatContainer
          chatMode={chatMode}
          currentUserId={currentUser?.id || ''}
          messages={messages}
          characters={characters}
          members={members}
          isMaster={isMaster}
          sharedDiceRolling={sharedDiceRolling}
          cerimoniaSuprimida={sharedDiceModal.isOpen}
          onEnviarRolagem={handleEnviarRolagem}
          loadingHistory={loadingHistory}
          onUpdateMessage={updateMessage}
          privateMessages={privateMessages}
          selectedPrivateUser={selectedPrivateUser}
          loadingPrivateHistory={loadingPrivateHistory}
          respondingAsNpc={respondingAsNpc}
          isUserOnline={isUserOnline}
          onBackToGroup={handleBackToGroup}
          onLoadMore={loadMoreMessages}
          loadingMore={loadingMoreMessages}
          hasMore={hasMoreMessages}
        />
      </div>
      {activeTab !== 'chat' && secondaryTabContent}
    </div>
  ), [activeTab, chatMode, currentUser?.id, messages, characters, members, isMaster, sharedDiceRolling, sharedDiceModal.isOpen, handleEnviarRolagem, loadingHistory, updateMessage, privateMessages, selectedPrivateUser, loadingPrivateHistory, respondingAsNpc, isUserOnline, handleBackToGroup, loadMoreMessages, loadingMoreMessages, hasMoreMessages, secondaryTabContent])

  // Early return DEPOIS dos hooks (secondaryTabContent/tabContent são
  // useMemo — não podem ficar após um return condicional).
  if (isLoading) {
    return (
      <div className="game-session-container flex items-center justify-center bg-game-primary">
        <Loader2 className="h-8 w-8 animate-spin text-game-accent" />
      </div>
    )
  }

  return (
    <TurnProvider value={turnContextValue}>
    <ChatProvider value={chatContextValue}>
    <div className="game-session-container h-screen w-full flex bg-game-primary overflow-hidden">
      {/* Banner de conexão perdida: só aparece quando a reconexão automática
          esgotou as tentativas (o indicador âmbar "Reconectando" cobre o
          período de tentativas). Oferece reconexão manual sem refresh. */}
      {!isConnected && !isReconnecting && connectionError && (
        <div
          role="alert"
          className="fixed top-3 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-3 px-4 py-2.5 rounded-lg bg-rose-600 text-white shadow-lg max-w-[92vw]"
        >
          <WifiOff className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm">Conexão perdida com o servidor.</span>
          <button
            type="button"
            onClick={() => reconnect()}
            className="text-sm font-semibold underline underline-offset-2 hover:no-underline focus:outline-none focus:ring-2 focus:ring-white/70 rounded px-1"
          >
            Reconectar
          </button>
        </div>
      )}

      {/* Renderiza UM layout por vez (useIsMobile, breakpoint 768 = md:).
          Antes ambos montavam simultaneamente, escondidos por CSS — o chat
          inteiro renderizava 2x a cada mensagem. As classes hidden/md:flex
          ficam como guarda visual no 1º frame (useIsMobile inicia false). */}
      {!isMobile ? (
      <div className="hidden md:flex w-full h-full">
        <DesktopLayout
          currentUser={currentUser || undefined}
          activeTab={activeTab}
          totalUnread={totalUnread}
          groupUnread={groupUnread}
          onTabChange={handleTabChange}
          onNavigate={navigate}
          members={members}
          characters={characters}
          tableName={table?.name}
          missionTitle={tituloDaMissao}
          userFullName={currentUser?.full_name || currentUser?.username || ''}
          userRole={currentUser ? isProfessorOrAdmin(currentUser) ? 'Mestre' : 'Jogador' : 'Jogador'}
          onCharacterChatClick={handleCharacterChatClick}
          currentUserId={currentUser?.id || ''}
          unreadCounts={unreadCounts}
          isLoading={isLoading}
          selectedPrivateUser={selectedPrivateUser}
          tableId={realTableId || undefined}
          isUserOnline={isUserOnline}
          onOpenSettings={openGameSettings}
          sorteioMediada={ceremonyMediada}
          dadosAtivos={dadosAtivos}
          onSortear={isMaster ? handleSortearCerimonia : undefined}
          sorteandoUserId={sorteandoUserId}
          chatDisabled={chatMuted}
        >
          {tabContent}
        </DesktopLayout>
      </div>
      ) : (
      <div className="md:hidden w-full h-full flex flex-col">
        {/* Fase B: MobileLayout é a coluna inteira (TopNavBar + conteúdo +
            input + BottomNavBar no fluxo), espelhando o DesktopLayout. */}
        <MobileLayout
          activeTab={activeTab}
          totalUnread={totalUnread}
          groupUnread={groupUnread}
          onTabChange={handleTabChange}
          members={members}
          characters={characters}
          tableName={table?.name}
          missionTitle={tituloDaMissao}
          currentUserId={currentUser?.id || ''}
          isLoading={isLoading}
          selectedPrivateUser={selectedPrivateUser}
          tableId={realTableId || undefined}
          onNavigate={navigate}
          onOpenSettings={openGameSettings}
          chatDisabled={chatMuted}
        >
          {tabContent}
        </MobileLayout>
      </div>
      )}

      {/* Overlays/modais globais — fora do branch mobile/desktop. Antes
          GameSettingsModal/LiveSyncViewer/PollViewer viviam dentro da div
          mobile e só funcionavam no desktop porque portais escapam do
          display:none; com renderização condicional precisam estar aqui. */}
      <GameSettingsModal
        isOpen={showGameSettings}
        onClose={closeGameSettings}
      />
      <LiveSyncViewer
        tableId={realTableId || ''}
        isMaster={isMaster}
        websocket={websocket}
        lastMessage={liveSyncMessage}
      />
      <PollViewer
        tableId={realTableId || ''}
        isMaster={isMaster}
        userId={currentUser?.id}
        lastMessage={pollMessage}
      />
      <LabSessionViewer
        tableId={realTableId || ''}
        isMaster={isMaster}
        userId={currentUser?.id}
        userFullName={currentUser?.full_name || currentUser?.username}
        lastMessage={labMessage}
      />

      {/* Revelação cerimonial do sorteio: a ficha do personagem sorteado abre
          para todos (somente leitura). Se a ficha é do próprio usuário, ele
          batiza o herói ali mesmo (nome; foto/descrição depois, no hub). */}
      <CharacterSheetEditor
        character={revealCharacter}
        tableId={realTableId || ''}
        open={!!revealCharacter}
        onOpenChange={(open) => { if (!open) setRevealCharacter(null) }}
        onSave={async () => {}}
        readOnly
        onRename={revealCharacter && currentUser && revealCharacter.userId === currentUser.id ? handleBatizar : undefined}
      />

      {isMaster && (
        <LiveSyncControl
          tableId={realTableId || ''}
          isOpen={isLiveSyncControlOpen}
          onOpenChange={setIsLiveSyncControlOpen}
        />
      )}

      {isMaster && (
        <PollControl
          tableId={realTableId || ''}
          isOpen={isPollControlOpen}
          onOpenChange={setIsPollControlOpen}
        />
      )}

      {isMaster && (
        <LabSessionControl
          tableId={realTableId || ''}
          isOpen={isLabControlOpen}
          onOpenChange={setIsLabControlOpen}
        />
      )}

      {/* Modal de Dados - Global (so existe com o modulo "dice" ativo na mesa;
          fica na raiz do componente, fora dos branches de layout) */}
      {dadosAtivos && (
      <DiceControls
        tableId={realTableId || ''}
        selectedCharacter={selectedCharacter}
        ownCharacter={meuPersonagem}
        rollDice={rollDice}
        showDiceModal={showDiceModal}
        sharedDiceModal={sharedDiceModal}
        sharedDiceRolling={sharedDiceRolling}
        souMestre={isMaster}
        mestreOnline={mestreOnline}
        repetida={jogadaRepetida}
        onDiceModalClose={handleDiceModalClose}
        onSharedDiceModalCloseRequest={handleSharedDiceModalCloseRequest}
      />
      )}
    </div>
    </ChatProvider>
    </TurnProvider>
  )
}
